import { useEffect, useMemo } from 'react';
import { Platform } from 'react-native';
import { PIN_RENDER_MAX_DELTA } from '@components/map/types';
import { useStructuresStore } from '../store/structuresStore';
import { usePostsStore } from '../store/postsStore';
import { useServiceAreasStore, POLYGON_RENDERING_ENABLED } from '../store/serviceAreasStore';
import { useAuthStore } from '../store/authStore';
import { useDefensePlansStore } from '../store/defensePlansStore';
import { suppressPairedBefores } from '../utils/postPairing';
import { MapRegion } from '../types';

interface UseMapSubscriptionsParams {
  serviceAreaIds: string[];
  serviceAreaIdsKey: string;
  activeAreaIdsKey: string;
  viewportKey: string;
  viewport: { south: number; west: number; north: number; east: number };
  currentRegion: MapRegion;
  serviceAreasLoaded: boolean;
  areas: { id: string }[];
  // The service area the map's viewport currently centers on (computed by
  // the screen), or null when the viewport is outside every service area.
  // Drives the defense-plans subscriptions below — see areaId in
  // src/schemas/firestoreSchemas.ts for why 'unassigned' is the fallback.
  currentAreaId: string | null;
}

interface UseMapSubscriptionsResult {
  floatingMapPosts: ReturnType<typeof usePostsStore.getState>['mapPosts'];
}

export function useMapSubscriptions({
  serviceAreaIds,
  serviceAreaIdsKey,
  activeAreaIdsKey,
  viewportKey,
  viewport,
  currentRegion,
  serviceAreasLoaded,
  areas,
  currentAreaId,
}: UseMapSubscriptionsParams): UseMapSubscriptionsResult {
  const loadAreasForViewport = useStructuresStore((s) => s.loadAreasForViewport);
  const subscribeToAreaClaims = useStructuresStore((s) => s.subscribeToAreaClaims);
  const unsubscribeAreaClaims = useStructuresStore((s) => s.unsubscribeAreaClaims);
  const subscribeOwnClaims = useStructuresStore((s) => s.subscribeOwnClaims);
  const unsubscribeOwnClaims = useStructuresStore((s) => s.unsubscribeOwnClaims);
  const subscribeToUserAdded = useStructuresStore((s) => s.subscribeToUserAdded);
  const unsubscribeUserAdded = useStructuresStore((s) => s.unsubscribeUserAdded);
  const evictAreasNotIn = useStructuresStore((s) => s.evictAreasNotIn);
  const ensureManifests = useStructuresStore((s) => s.ensureManifests);
  const ensurePolygonsLoaded = useServiceAreasStore((s) => s.ensurePolygonsLoaded);

  const user = useAuthStore((s) => s.user);

  const mapPosts = usePostsStore((s) => s.mapPosts);
  const subscribeMapPosts = usePostsStore((s) => s.subscribeMapPosts);
  const unsubscribeMapPosts = usePostsStore((s) => s.unsubscribeMapPosts);

  const subscribeAreaPlans = useDefensePlansStore((s) => s.subscribeAreaPlans);
  const unsubscribeAreaPlans = useDefensePlansStore((s) => s.unsubscribeAreaPlans);
  const subscribeOwnPlan = useDefensePlansStore((s) => s.subscribeOwnPlan);
  const unsubscribeOwnPlan = useDefensePlansStore((s) => s.unsubscribeOwnPlan);

  // When `areas` changes (e.g. Firestore toggled an area inactive), evict
  // any in-memory shards for areas no longer in the active list so the
  // map stops rendering their pins. Keyed on the stable id list so we
  // don't evict just because a name or timestamp changed.
  useEffect(() => {
    evictAreasNotIn(areas.map((a) => a.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAreaIdsKey]);

  // Always pull the manifest for any service area the viewport touches.
  // The manifest is tiny (< 100KB) and is the bbox prefilter that decides
  // which shards to fetch once the user zooms in. Independent from
  // `pinsVisible` — we need the manifest *before* zooming in.
  useEffect(() => {
    if (!serviceAreasLoaded) return;
    ensureManifests(serviceAreaIds).catch(() => {
      // failures already logged in the store
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceAreaIdsKey, serviceAreasLoaded, ensureManifests]);

  // Load shards that overlap the viewport. Debounced so rapid pans coalesce
  // into one load call once the map settles. Shards already cached in the
  // store are skipped automatically. No buffer — fetch only what's on
  // screen to keep the per-pan batch small; short pans into not-yet-loaded
  // cells will briefly show blank, then fill in once the next load fires.
  //
  // Gated on `pinsVisible`: at wide zoom the native Map renders no
  // structure markers (observations-first — the wide view leads with
  // observation dots), so there's no reason to pay the fetch/parse cost.
  // On web we keep fetching at every zoom: rendering is gated inside
  // Map.web.tsx instead, because address search reads the in-memory shard
  // cache and would come up empty at wide zoom if the fetch were gated too.
  const pinsVisible = Platform.OS === 'web' || currentRegion.latitudeDelta <= PIN_RENDER_MAX_DELTA;
  useEffect(() => {
    if (!serviceAreasLoaded) return;
    if (!pinsVisible) return;
    const handle = setTimeout(() => {
      loadAreasForViewport({
        areaIds: serviceAreaIds,
        south: viewport.south,
        west: viewport.west,
        north: viewport.north,
        east: viewport.east,
      }).catch(() => {
        // error state already set in the store
      });
    }, 200);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceAreaIdsKey, viewportKey, pinsVisible, serviceAreasLoaded, loadAreasForViewport]);

  // Lazy-load the polygon outline for any area that's now in view. The
  // loader memos 404s and in-flight fetches so this is safe to call on
  // every serviceAreaIds change. Gated on POLYGON_RENDERING_ENABLED so we
  // don't pay the fetch cost while the feature is off.
  useEffect(() => {
    if (!POLYGON_RENDERING_ENABLED) return;
    ensurePolygonsLoaded(serviceAreaIds).catch(() => {
      // failures are logged in the store; nothing to surface to the user
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceAreaIdsKey]);

  // Claims live in Firestore behind an auth-required rule. Only subscribe
  // once the user is signed in — anonymous browsers just see structures
  // without claim coloring, which is fine.
  useEffect(() => {
    if (!user) return;
    subscribeToAreaClaims(serviceAreaIds);
    return () => {
      unsubscribeAreaClaims();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, serviceAreaIdsKey, subscribeToAreaClaims, unsubscribeAreaClaims]);

  // This user's own claims, direct from `structureClaims` rather than the
  // CF-derived area summary — faster and covers claims made on another
  // device. Keyed on user id (not the whole user object) so it doesn't
  // re-subscribe on unrelated profile field changes.
  useEffect(() => {
    if (!user?.id) return;
    subscribeOwnClaims(user.id);
    return () => {
      unsubscribeOwnClaims();
    };
  }, [user?.id, subscribeOwnClaims, unsubscribeOwnClaims]);

  // Live-stream user-added structures alongside the static GeoJSON shards
  // so a property created via addUserStructure shows up in search and on
  // the map without waiting for the next export. Global (not area-scoped):
  // service areas are a backend-only concept right now. /structures is
  // world-readable, so this subscribes unconditionally — gating on `user`
  // would hide user-added properties from anonymous visitors and from
  // every other signed-in user too.
  useEffect(() => {
    subscribeToUserAdded();
    return () => {
      unsubscribeUserAdded();
    };
  }, [subscribeToUserAdded, unsubscribeUserAdded]);

  // Live subscription to recent observations so the map can render blue dots
  // for posts that aren't attached to a structure. Structure-linked posts are
  // skipped at the filter step below — they're represented by structure pins
  // and surface through the structure detail sheet.
  useEffect(() => {
    subscribeMapPosts();
    return () => {
      unsubscribeMapPosts();
    };
  }, [subscribeMapPosts, unsubscribeMapPosts]);

  // Area plans (shared defense plans) are world-readable, same as
  // user-added structures above — subscribe unconditionally, gated only on
  // having a resolved area. 'unassigned' is a valid areaId (the fallback
  // claims and plans both use when a point falls outside every service
  // area), so it still subscribes rather than going idle.
  useEffect(() => {
    subscribeAreaPlans(currentAreaId ?? 'unassigned');
    return () => {
      unsubscribeAreaPlans();
    };
  }, [currentAreaId, subscribeAreaPlans, unsubscribeAreaPlans]);

  // The signed-in owner's own plan, by owner only (not by the area in
  // view; see defensePlansApi.subscribeOwnPlan). Anonymous Fire Scouts
  // never reach the draw tool (both entry points gate on requireAuth), so
  // there's no own-plan doc to subscribe to for them — skip rather than pay
  // for a query that would only ever return empty.
  useEffect(() => {
    if (!user?.id || user.isAnonymous) return;
    subscribeOwnPlan(user.id);
    return () => {
      unsubscribeOwnPlan();
    };
  }, [user?.id, user?.isAnonymous, subscribeOwnPlan, unsubscribeOwnPlan]);

  const floatingMapPosts = useMemo(() => {
    // Suppress against the FULL mapPosts array, not the pin-eligible subset
    // below: a structure-linked after post has no floating pin of its own
    // but must still suppress its before (see postPairing.ts).
    const eligible = mapPosts.filter(
      (p) =>
        p.location && !p.structureId && (p.visibility === 'public' || p.visibility === undefined)
    );
    return suppressPairedBefores(eligible, mapPosts);
  }, [mapPosts]);

  return { floatingMapPosts };
}
